import { CommonModule } from "@angular/common";
import { Component, inject, signal } from "@angular/core";
import { ActivatedRoute, RouterModule } from "@angular/router";
import { TranslateModule } from "@ngx-translate/core";
import { StudentCourseService } from "src/app/services/api/student-course.service";
import { MonitorService } from "src/app/services/api/monitor.service";
import { environment } from "src/environments/environment";
import { SharedModule } from "src/shared/shared.module";
import { PipesModule } from "src/utils/pipes/pipes.module";

/**
 * Which courses this student is taking, and how far into each one they are.
 *
 * The 2021 build opened the parent's area on exactly this, and it was the one
 * thing the rebuild had no equivalent for: a parent could see how long their
 * child had studied, what they had scored and which topics were weak, but not
 * the plain question they actually ask first — "what is she taking, and has she
 * finished any of it?"
 *
 * Reads the same endpoint the study record's course filter already reads, so
 * nothing new is asked of the API.
 */
@Component({
  selector: "app-course-progress",
  imports: [SharedModule, CommonModule, RouterModule, TranslateModule, PipesModule],
  templateUrl: "./course-progress.component.html",
  styleUrl: "./course-progress.component.scss",
})
export class CourseProgressComponent {
  #studentCourseService = inject(StudentCourseService);
  #monitorService = inject(MonitorService);
  #route = inject(ActivatedRoute);

  fileUrl = environment.apiFileUrl;

  isLoading = signal(true);
  courses = signal<any[]>([]);

  ngOnInit(): void {
    // Same rule as the other tabs: the child is named in the query string, so a
    // reload or a bookmark shows the right person's courses rather than the
    // first one the picker happens to resolve.
    this.#route.queryParamMap.subscribe(() => this.load());
  }

  private load(): void {
    this.isLoading.set(true);

    const fromUrl = this.#route.snapshot.queryParamMap.get("student_id");
    const studentId =
      this.#monitorService.monitorStudent()?.id ??
      (fromUrl ? Number(fromUrl) : null);

    const filter: any = { is_activated: true, limit: 50 };
    if (studentId) filter.student_id = studentId;

    this.#studentCourseService.getStudentCourse(filter).subscribe({
      next: (res: any) => {
        this.courses.set(res?.data ?? res ?? []);
        this.isLoading.set(false);
      },
      error: () => {
        this.courses.set([]);
        this.isLoading.set(false);
      },
    });
  }

  /** 0-100, clamped: the API stores progress as a percentage but nothing stops
   *  a rounding artefact putting it a hair over. */
  progressOf(course: any): number {
    return Math.max(0, Math.min(100, Math.round(Number(course?.progress) || 0)));
  }

  /** The bar takes its colour from how far along the course is, on the same
   *  three bands the skills report uses for mastery. */
  bandOf(course: any): string {
    const value = this.progressOf(course);
    if (value >= 80) return "is-good";
    if (value >= 40) return "is-mid";
    return "is-low";
  }
}
